import { useCallback, useEffect, useRef, useState } from 'react';
import type { Question } from './grammar/topics';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// A lesson left part-way (grammar or story), so coming back picks up exactly
// where the student was: which questions are done, which were missed, the
// order of what's left, and the hearts. Kept in this browser AND on the
// student's account (lesson_saves, 20261045), so it follows them from school to
// home; whichever was saved last wins.
//
// A grammar save carries its questions too (they're made at random, so the same
// lesson has to come back). A story lesson's never does: its questions hold
// Google's translations, which are never stored in the database. They're
// rebuilt from the story instead (the same every time for a read), and the
// progress is matched up by question key.
// ─────────────────────────────────────────────────────────────────────────────

export type LessonSave = {
  done: string[]; first: string[]; missed: string[];
  /** What's left, in order (misses already moved to where they come back). */
  queue?: string[];
  hearts?: number;
  /** Grammar only (see above). */
  questions?: Question[];
  /** When it was saved (ms). */
  at: number;
};

const localKey = (assignmentId: string, pass: number) => `lessonSave:${assignmentId}:${pass}`;
/** Story lessons kept their progress here before 20261045. */
const legacyKey = (assignmentId: string, pass: number) => `storyLesson:${assignmentId}:${pass}`;

const valid = (v: unknown): v is LessonSave => !!v && typeof v === 'object' && Array.isArray((v as LessonSave).done);

function readLocal(assignmentId: string, pass: number): LessonSave | null {
  try {
    const v = JSON.parse(localStorage.getItem(localKey(assignmentId, pass)) ?? 'null');
    if (valid(v)) return v;
    const old = JSON.parse(localStorage.getItem(legacyKey(assignmentId, pass)) ?? 'null');
    if (valid(old)) return { ...old, at: old.at ?? 0 };
  } catch { /* private mode */ }
  return null;
}

async function readRemote(assignmentId: string, pass: number): Promise<LessonSave | null> {
  try {
    const { data, error } = await supabase.from('lesson_saves').select('data').eq('assignment_id', assignmentId).eq('pass', pass).maybeSingle();
    if (error || !data) return null;
    return valid(data.data) ? data.data : null;
  } catch { return null; }
}

/**
 * The save for one pass of one assignment (students only; `enabled` false for
 * the teacher's preview, which never saves). `saved` is undefined until loaded.
 */
export function useLessonSave(assignmentId: string, pass: number, studentId: string | undefined, enabled: boolean) {
  const [saved, setSaved] = useState<LessonSave | null | undefined>(enabled ? undefined : null);
  const pending = useRef<LessonSave | null | 'none'>('none');
  const timer = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) { setSaved(null); return null; }
    const local = readLocal(assignmentId, pass);
    const remote = await readRemote(assignmentId, pass);
    const best = local && remote ? (remote.at > local.at ? remote : local) : local ?? remote;
    setSaved(best);
    return best;
  }, [assignmentId, pass, enabled]);
  useEffect(() => { void load(); }, [load]);

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    const p = pending.current;
    pending.current = 'none';
    if (p === 'none' || !studentId) return;
    if (p === null) void supabase.from('lesson_saves').delete().eq('assignment_id', assignmentId).eq('student_id', studentId).eq('pass', pass).then(() => {}, () => {});
    else void supabase.from('lesson_saves').upsert({ assignment_id: assignmentId, student_id: studentId, pass, data: p, updated_at: new Date().toISOString() }).then(() => {}, () => {});
  }, [assignmentId, pass, studentId]);

  /** Save progress (null = the lesson is over: forget it). The account copy is written a moment later, batched. */
  const save = useCallback((s: LessonSave | null) => {
    if (!enabled) return;
    try {
      if (s) localStorage.setItem(localKey(assignmentId, pass), JSON.stringify(s));
      else { localStorage.removeItem(localKey(assignmentId, pass)); localStorage.removeItem(legacyKey(assignmentId, pass)); }
    } catch { /* full: the account copy still goes */ }
    setSaved(s);
    pending.current = s;
    window.clearTimeout(timer.current);
    if (s === null) flush();
    else timer.current = window.setTimeout(flush, 1500);
  }, [assignmentId, pass, enabled, flush]);

  // Leaving the page (or the tab going away): send what's waiting now.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => { document.removeEventListener('visibilitychange', onHide); window.removeEventListener('pagehide', flush); flush(); };
  }, [flush]);

  return { saved, save, reload: load };
}
